import { useState } from "react";
import { usePendingAcceptanceQueue } from "../hooks/useSowingInstructions";
import type { SowingInstructionDto } from "../services/sowingInstructionsApi";
import { AcceptSIDialog } from "../components/sowingInstructions/PhaseWorkflowDialogs";
import {
  RejectInstructionDialog,
  AmendInstructionDialog,
} from "../components/sowingInstructions/SowingLifecycleDialogs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { format, parseISO } from "date-fns";
import { useAuthStore } from "@/features/auth/stores/authStore";
import { useUnitsOfMeasure } from "@/shared/hooks/useUnitsOfMeasure";

export const PendingAcceptancePage = () => {
  const queueQuery = usePendingAcceptanceQueue();
  const { hasPermission } = useAuthStore();
  const unitsQuery = useUnitsOfMeasure();
  const canAccept = hasPermission("sowing_instructions.accept");
  const [selected, setSelected] = useState<SowingInstructionDto | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [amendDialogOpen, setAmendDialogOpen] = useState(false);

  const handleAccept = (instruction: SowingInstructionDto) => {
    setSelected(instruction);
    setDialogOpen(true);
  };

  const handleReject = (instruction: SowingInstructionDto) => {
    setSelected(instruction);
    setRejectDialogOpen(true);
  };

  const handleAmend = (instruction: SowingInstructionDto) => {
    setSelected(instruction);
    setAmendDialogOpen(true);
  };

  const handleSuccess = () => {
    setDialogOpen(false);
    setRejectDialogOpen(false);
    setAmendDialogOpen(false);
    setSelected(null);
    queueQuery.refetch();
  };

  const rows = queueQuery.data ?? [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Pending Acceptance</h1>
        <p className="text-muted-foreground mt-1">
          Sowing instructions submitted by Sales that are awaiting your review and acceptance.
        </p>
      </div>

      {queueQuery.isLoading && (
        <div className="flex items-center justify-center h-64">
          <p>Loading queue...</p>
        </div>
      )}

      {queueQuery.error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <p className="text-red-600">Failed to load pending queue</p>
        </div>
      )}

      {!queueQuery.isLoading && rows.length === 0 && (
        <div className="rounded-lg border bg-muted/40 p-8 text-center">
          <p className="text-muted-foreground">No sowing instructions pending acceptance</p>
        </div>
      )}

      {rows.length > 0 && (
        <div className="rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Instruction #</TableHead>
                <TableHead>Crop Type</TableHead>
                <TableHead>Variety</TableHead>
                <TableHead>Tray Type</TableHead>
                <TableHead>Quantity</TableHead>
                <TableHead>Purpose</TableHead>
                <TableHead>Submitted</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((inst) => (
                <TableRow key={inst.id}>
                  <TableCell className="font-mono text-sm">{inst.instructionNumber}</TableCell>
                  <TableCell>{inst.cropTypeName}</TableCell>
                  <TableCell>{inst.variety}</TableCell>
                  <TableCell>
                    <Badge variant="outline">{(inst as any).trayType ?? "—"}</Badge>
                  </TableCell>
                  <TableCell>{inst.quantityToSow} {unitsQuery.data?.find(u => u.value === inst.unit)?.name || ""}</TableCell>
                  <TableCell>
                    <Badge variant="secondary">
                      {inst.purpose === 1 ? "Customer Order" : inst.purpose === 2 ? "Company Stock" : `Purpose ${inst.purpose}`}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm">
                    {inst.createdAt ? format(parseISO(inst.createdAt), "dd MMM yyyy") : "—"}
                  </TableCell>
                  <TableCell className="text-right">
                    {canAccept ? (
                      <div className="flex items-center justify-end gap-1">
                        <Button size="sm" variant="destructive" onClick={() => handleReject(inst)}>
                          Reject
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => handleAmend(inst)}>
                          Amend
                        </Button>
                        <Button size="sm" onClick={() => handleAccept(inst)}>
                          Accept
                        </Button>
                      </div>
                    ) : (
                      <span className="text-sm text-muted-foreground">View only</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {selected && (
        <AcceptSIDialog
          instruction={selected}
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          onSuccess={handleSuccess}
        />
      )}

      {selected && (
        <RejectInstructionDialog
          instruction={{ id: selected.id, variety: selected.variety ?? "" }}
          open={rejectDialogOpen}
          onOpenChange={setRejectDialogOpen}
          onSuccess={handleSuccess}
        />
      )}

      {selected && (
        <AmendInstructionDialog
          instruction={{
            id: selected.id,
            variety: selected.variety ?? "",
            quantityToSow: selected.quantityToSow,
            scheduledSowingDate: selected.scheduledSowingDate,
          }}
          open={amendDialogOpen}
          onOpenChange={setAmendDialogOpen}
          onSuccess={handleSuccess}
        />
      )}
    </div>
  );
};
